
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import PageLayout from "@/components/layout/PageLayout";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import PasswordSetupForm from "@/components/auth/PasswordSetupForm";
import { AppRole } from "@/types/database/auth";

const SignUpPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  
  const encodedEmail = searchParams.get("email");
  const email = encodedEmail ? decodeURIComponent(encodedEmail) : null;
  const firstName = searchParams.get("firstName") ? decodeURIComponent(searchParams.get("firstName")!) : null;
  const lastName = searchParams.get("lastName") ? decodeURIComponent(searchParams.get("lastName")!) : null;
  const phone = searchParams.get("phone") ? decodeURIComponent(searchParams.get("phone")!) : null;
  const roleParam = searchParams.get("role");
  const role = roleParam ? decodeURIComponent(roleParam) as AppRole : "user";

  useEffect(() => {
    console.log("SignUpPage mounted");
    console.log("Encoded email:", encodedEmail);
    console.log("Decoded email:", email);
    console.log("First name:", firstName);
    console.log("Last name:", lastName);
    console.log("Phone:", phone);
    console.log("Role:", role);

    if (!email) {
      console.log("No email provided, redirecting to auth page");
      navigate("/auth");
      return;
    }

    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        console.log("User already has a session, redirecting to dashboard");
        navigate("/dashboard");
      }
    };

    checkSession();
  }, [email, navigate]);

  if (!email) {
    return null;
  }

  return (
    <PageLayout>
      <div className="container max-w-md mx-auto py-8">
        <Card className="p-8">
          <h1 className="text-2xl font-bold mb-6 text-center">Set Your Password</h1>
          <PasswordSetupForm 
            email={email}
            firstName={firstName}
            lastName={lastName}
            phone={phone}
            role={role}
            isLoading={isLoading}
            onSubmit={async (values) => {
              try {
                setIsLoading(true);
                console.log("Starting signup process for email:", email);
                console.log("With first name:", firstName);
                console.log("With last name:", lastName);
                console.log("With phone:", phone);

                // First attempt to delete any existing user with this email
                console.log("Checking and cleaning up any existing user...");
                const { data, error: cleanupError } = await supabase.functions.invoke('delete-user-by-email', {
                  body: { email }
                });

                if (cleanupError) {
                  console.error("Error during cleanup:", cleanupError);
                  throw cleanupError;
                }

                // Get existing profile if it exists
                const existingProfile = data?.existingProfile;
                console.log("Existing profile data:", existingProfile);

                // Small delay to ensure cleanup is complete
                await new Promise(resolve => setTimeout(resolve, 1000));

                // Create user metadata object with all available user data
                const userMetadata = {
                  first_name: firstName || existingProfile?.first_name || '',
                  last_name: lastName || existingProfile?.last_name || '',
                  phone: phone || existingProfile?.phone || ''
                };
                
                console.log("Creating user with metadata:", userMetadata);

                // Now attempt signup
                console.log("Proceeding with signup...");
                const { data: signupData, error: signUpError } = await supabase.auth.signUp({
                  email,
                  password: values.password,
                  options: {
                    data: userMetadata
                  }
                });

                if (signUpError) {
                  console.error("Error during signup:", signUpError);
                  throw signUpError;
                }

                console.log("Signup successful, user:", signupData?.user?.id);
                console.log("User metadata:", signupData?.user?.user_metadata);

                // Update role if a user was created and role was provided
                if (signupData?.user && role) {
                  const { error: roleError } = await supabase
                    .rpc('update_user_role_safely', {
                      user_id_input: signupData.user.id,
                      new_role: role
                    });

                  if (roleError) {
                    console.error("Error setting user role:", roleError);
                    // Continue despite role error
                  }
                }

                // Ensure profile data is correctly updated
                if (signupData?.user) {
                  const profileData = {
                    id: signupData.user.id,
                    first_name: firstName || existingProfile?.first_name || '',
                    last_name: lastName || existingProfile?.last_name || '',
                    email: email,
                    phone: phone || existingProfile?.phone || '',
                    street: existingProfile?.street || '',
                    city: existingProfile?.city || '',
                    state: existingProfile?.state || '',
                    zip_code: existingProfile?.zip_code || ''
                  };
                  
                  console.log("Explicitly updating profile with data:", profileData);
                  
                  const { error: profileError } = await supabase
                    .from('profiles')
                    .upsert(profileData);
                    
                  if (profileError) {
                    console.error("Error updating profile:", profileError);
                    // Continue despite profile error
                  } else {
                    console.log("Profile updated successfully");
                  }
                  
                  // Verify profile data was saved correctly
                  const { data: verifyProfile, error: verifyError } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', signupData.user.id)
                    .single();
                    
                  if (verifyError) {
                    console.error("Error verifying profile:", verifyError);
                  } else {
                    console.log("Verified profile data:", verifyProfile);
                    console.log("Verified first name:", verifyProfile.first_name);
                    console.log("Verified last name:", verifyProfile.last_name);
                    console.log("Verified phone:", verifyProfile.phone);
                  }
                }

                toast({
                  title: "Account created successfully",
                  description: "You can now sign in with your email and password.",
                });

                navigate("/auth", { replace: true });
              } catch (error: any) {
                console.error("Error during password setup:", error);
                toast({
                  variant: "destructive",
                  title: "Error",
                  description: error.message || "Failed to set password",
                });
              } finally {
                setIsLoading(false);
              }
            }}
          />
        </Card>
      </div>
    </PageLayout>
  );
};

export default SignUpPage;
